import type { AppConfig, Company } from './types'

// ---------------------------------------------------------------------------
// Runtime brand config (§5). Owner preference is deploy-time config (Appendix E):
// we fetch a static /config.json shipped alongside the build. One build → many
// brands by swapping that file per deploy. An injected window.__APP_CONFIG__
// blob is also honored if present (zero extra request).
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    __APP_CONFIG__?: AppConfig
  }
}

let cached: AppConfig | null = null

export async function loadConfig(): Promise<AppConfig> {
  if (cached) return cached

  // 1. Injected blob (deploy-time write into index.html) takes precedence.
  if (window.__APP_CONFIG__) {
    cached = window.__APP_CONFIG__
    applyBranding(cached)
    return cached
  }

  // 2. Static deploy-time config file. One deployment can serve multiple brands/
  //    verticals: `?config=<slug>` loads `/config.<slug>.json` (e.g.
  //    ?config=behavioral-health), and the bare URL loads `/config.json`.
  //    Unknown/missing slugs fall back to the default config.
  const primary = configUrl()
  let res = await fetch(primary, { headers: { Accept: 'application/json' } })
  if (!res.ok && primary !== '/config.json') {
    res = await fetch('/config.json', { headers: { Accept: 'application/json' } })
  }
  if (!res.ok) throw new Error(`config load failed (${res.status})`)
  cached = (await res.json()) as AppConfig
  applyBranding(cached)
  return cached
}

/** Which config file to load, from the `?config=<slug>` URL param. */
function configUrl(): string {
  const raw = new URLSearchParams(window.location.search).get('config')
  // Whitelist to a safe slug so we can only fetch sibling config files.
  const slug = raw && /^[a-z0-9-]{1,40}$/.test(raw) ? raw : null
  return slug ? `/config.${slug}.json` : '/config.json'
}

/**
 * Compute the brand CSS custom properties for a company. Pure — returns a map
 * of `--brand-*` / `--page-bg` vars applied to `:root` by `applyBranding`.
 */
function computeBrandVars(company: Company): Record<string, string> {
  const vars: Record<string, string> = {}
  const set = (name: string, value?: string) => {
    if (value) vars[name] = value
  }

  // Brand blue (primary surfaces, links), yellow CTA, dark gray, green status.
  set('--brand-primary', company.color_primary)
  set('--brand-cta', company.color_cta)
  set('--brand-dark', company.color_dark)
  set('--brand-green', company.color_green || company.color_secondary)
  set('--brand-secondary', company.color_secondary)
  // Page background behind the hero card — defaults to the brand primary.
  set('--page-bg', company.color_background || company.color_primary)

  if (company.color_primary) {
    set('--brand-primary-contrast', contrastColor(company.color_primary))
  }
  // Text color that reads on the CTA color (dark on yellow per the design).
  if (company.color_cta) {
    set('--brand-cta-text', company.color_dark || contrastColor(company.color_cta))
  }
  return vars
}

/** Push brand colors into CSS custom properties + set title/favicon. */
function applyBranding(config: AppConfig): void {
  const { company } = config
  const root = document.documentElement
  const vars = computeBrandVars(company)
  for (const [name, value] of Object.entries(vars)) root.style.setProperty(name, value)

  if (company.name) document.title = `Book an appointment — ${company.name}`

  if (company.icon) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = company.icon
  }
}

/** Black or white text for a given hex background, for accessible button labels. */
function contrastColor(hex: string): string {
  const h = hex.replace('#', '')
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  if ([r, g, b].some(Number.isNaN)) return '#ffffff'
  // Perceived luminance (sRGB).
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? '#1a1a1a' : '#ffffff'
}
